
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { sanitizeTextContent } from '@/utils/sanitizeHtml';

interface SponsorContactFormProps {
  isOpen: boolean;
  onClose: () => void;
  selectedTier?: string;
}

const SponsorContactForm: React.FC<SponsorContactFormProps> = ({ isOpen, onClose, selectedTier }) => {
  const [formData, setFormData] = useState({
    organizationName: '',
    contactName: '',
    email: '',
    phone: '',
    organizationType: '',
    organizationTypeOther: '',
    geographicFocus: '',
    message: '',
    agreeToContact: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Lock background scroll when modal is open to avoid multiple scrollbars
  useEffect(() => {
    if (isOpen) {
      const prevBodyOverflow = document.body.style.overflow;
      const prevHtmlOverflow = document.documentElement.style.overflow;
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prevBodyOverflow;
        document.documentElement.style.overflow = prevHtmlOverflow;
      };
    }
  }, [isOpen]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Required field validations
    if (!formData.organizationName.trim()) {
      newErrors.organizationName = 'Organization name is required';
    } else if (formData.organizationName.trim().length < 2) {
      newErrors.organizationName = 'Organization name must be at least 2 characters';
    }

    if (!formData.contactName.trim()) {
      newErrors.contactName = 'Contact name is required';
    } else if (formData.contactName.trim().length < 2) {
      newErrors.contactName = 'Contact name must be at least 2 characters';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Phone validation (optional but must be valid if provided)
    if (formData.phone.trim()) {
      const phoneRegex = /^[\+]?[\s\.\-\(\)]*([0-9][\s\.\-\(\)]*){10,}$/;
      if (!phoneRegex.test(formData.phone.trim())) {
        newErrors.phone = 'Please enter a valid phone number';
      }
    }

    if (!formData.organizationType) {
      newErrors.organizationType = 'Organization type is required';
    } else if (formData.organizationType === 'other' && !formData.organizationTypeOther.trim()) {
      newErrors.organizationTypeOther = 'Please specify your organization type';
    }

    // Message length validation
    if (formData.message.trim().length > 1000) {
      newErrors.message = 'Message must be less than 1000 characters';
    }

    if (!formData.agreeToContact) {
      newErrors.agreeToContact = 'You must agree to be contacted to continue';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fix the errors in the form.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Sanitize all inputs
      const sanitizedData = {
        organization_name: sanitizeTextContent(formData.organizationName.trim()),
        contact_name: sanitizeTextContent(formData.contactName.trim()),
        email: formData.email.trim().toLowerCase(),
        phone: sanitizeTextContent(formData.phone.trim()),
        organization_type: formData.organizationType === 'other' 
          ? `Other: ${sanitizeTextContent(formData.organizationTypeOther.trim())}` 
          : formData.organizationType,
        geographic_focus: sanitizeTextContent(formData.geographicFocus.trim()),
        message: sanitizeTextContent(formData.message.trim()),
        agree_to_contact: formData.agreeToContact
      };

      const params = new URLSearchParams(window.location.search);
      const referralCode = params.get('ref') || 
                          params.get('referral') || 
                          localStorage.getItem('referral_code') || 
                          '';
      
      const payload = {
        ...sanitizedData,
        page_path: window.location.pathname,
        referrer: document.referrer || '',
        utm_source: params.get('utm_source') || sessionStorage.getItem('utm_source') || '',
        utm_medium: params.get('utm_medium') || sessionStorage.getItem('utm_medium') || '',
        utm_campaign: params.get('utm_campaign') || sessionStorage.getItem('utm_campaign') || '',
        utm_content: params.get('utm_content') || sessionStorage.getItem('utm_content') || '',
        utm_term: params.get('utm_term') || sessionStorage.getItem('utm_term') || '',
        referral_code: referralCode,
        source: 'website'
      };

      const { data: result, error } = await supabase.functions.invoke('submit-sponsor-inquiry', {
        body: payload
      });

      if (error || (result && result.success === false)) throw (error || new Error(result?.message || 'Submission failed'));
      
      setIsSubmitted(true);
      toast({
        title: "Contact Request Submitted!",
        description: "Thank you for your interest. Our Partnership team will contact you within one business day.",
      });
    } catch (error: any) {
      console.error('Sponsor form submission error:', error);
      toast({
        title: "Submission Error",
        description: "There was an error submitting your request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      organizationName: '',
      contactName: '',
      email: '',
      phone: '',
      organizationType: '',
      organizationTypeOther: '',
      geographicFocus: '',
      message: '',
      agreeToContact: false,
    });
    setIsSubmitted(false);
    setErrors({});
  };

  if (!isOpen) return null;

  if (isSubmitted) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <Card className="w-full max-w-md mx-auto">
          <CardContent className="pt-8 text-center">
            <div className="flex flex-col items-center space-y-4">
              <CheckCircleIcon className="h-16 w-16 text-primary" />
              <h3 className="text-2xl font-bold text-foreground">Request Submitted!</h3>
              <p className="text-muted-foreground">
                Thank you for your interest in SeniorThrive sponsorship opportunities. 
                Our Partnership team will contact you within one business day.
              </p>
              <div className="flex gap-3">
                <Button 
                  onClick={() => {
                    resetForm();
                    onClose();
                  }}
                  className="bg-primary hover:bg-primary/90"
                >
                  Close
                </Button>
                <Button 
                  onClick={resetForm}
                  variant="outline"
                >
                  Submit Another Request
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-2 sm:p-4 z-50 overflow-hidden">
      <Card className="w-full max-w-2xl mx-auto my-2 sm:my-8 max-h-[95vh] overflow-y-auto">
        <CardHeader className="relative">
          <Button
            onClick={onClose}
            variant="ghost"
            size="sm"
            className="absolute right-4 top-4 h-8 w-8 p-0"
          >
            <XMarkIcon className="h-4 w-4" />
          </Button>
          <CardTitle className="text-2xl font-bold">Start Your Partnership</CardTitle>
          <CardDescription>
            Let's discuss how SeniorThrive can help you become essential to your clients' well-being
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Organization Information */}
            <div className="space-y-2">
              <Label htmlFor="organizationName">Organization Name *</Label>
              <Input
                id="organizationName"
                type="text"
                value={formData.organizationName}
                onChange={(e) => setFormData(prev => ({ ...prev, organizationName: e.target.value }))}
                maxLength={200}
                className={errors.organizationName ? 'border-red-500' : ''}
                required
              />
              {errors.organizationName && (
                <p className="text-red-500 text-sm">{errors.organizationName}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="contactName">Contact Name *</Label>
              <Input
                id="contactName"
                type="text"
                value={formData.contactName}
                onChange={(e) => setFormData(prev => ({ ...prev, contactName: e.target.value }))}
                maxLength={100}
                className={errors.contactName ? 'border-red-500' : ''}
                required
              />
              {errors.contactName && (
                <p className="text-red-500 text-sm">{errors.contactName}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  maxLength={254}
                  className={errors.email ? 'border-red-500' : ''}
                  required
                />
                {errors.email && (
                  <p className="text-red-500 text-sm">{errors.email}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  maxLength={20}
                  className={errors.phone ? 'border-red-500' : ''}
                />
                {errors.phone && (
                  <p className="text-red-500 text-sm">{errors.phone}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="organizationType">Organization Type *</Label>
              <Select 
                value={formData.organizationType} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, organizationType: value, organizationTypeOther: value !== 'other' ? '' : prev.organizationTypeOther }))}
              >
                <SelectTrigger className={errors.organizationType ? 'border-red-500' : ''}>
                  <SelectValue placeholder="Select organization type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="healthcare">Healthcare Provider</SelectItem>
                  <SelectItem value="financial">Financial Services</SelectItem>
                  <SelectItem value="insurance">Insurance Company</SelectItem>
                  <SelectItem value="nonprofit">Non-Profit Organization</SelectItem>
                  <SelectItem value="technology">Technology Company</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
              {errors.organizationType && (
                <p className="text-red-500 text-sm">{errors.organizationType}</p>
              )}
            </div>

            {formData.organizationType === 'other' && (
              <div className="space-y-2">
                <Label htmlFor="organizationTypeOther">Please Specify *</Label>
                <Input
                  id="organizationTypeOther"
                  type="text"
                  placeholder="Enter your organization type"
                  value={formData.organizationTypeOther}
                  onChange={(e) => setFormData(prev => ({ ...prev, organizationTypeOther: e.target.value }))}
                  maxLength={100}
                  className={errors.organizationTypeOther ? 'border-red-500' : ''}
                />
                {errors.organizationTypeOther && (
                  <p className="text-red-500 text-sm">{errors.organizationTypeOther}</p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="geographicFocus">Geographic Focus</Label>
              <Input
                id="geographicFocus"
                type="text"
                placeholder="e.g., California, National, International"
                value={formData.geographicFocus}
                onChange={(e) => setFormData(prev => ({ ...prev, geographicFocus: e.target.value }))}
                maxLength={100}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                placeholder="Tell us about your organization and partnership goals..."
                value={formData.message}
                onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                maxLength={1000}
                className={errors.message ? 'border-red-500' : ''}
                rows={3}
              />
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>{errors.message && <span className="text-red-500">{errors.message}</span>}</span>
                <span>{formData.message.length}/1000</span>
              </div>
            </div>

            {/* Agreement */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="contact"
                checked={formData.agreeToContact}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, agreeToContact: checked as boolean }))}
                required
              />
              <Label htmlFor="contact" className="text-sm">
                I agree to be contacted by the SeniorThrive Partnership team *
              </Label>
            </div>
            {errors.agreeToContact && (
              <p className="text-red-500 text-sm">{errors.agreeToContact}</p>
            )}

            {/* Submit Button */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button 
                type="submit" 
                className="flex-1 bg-primary hover:bg-primary/90 text-white shadow-feature text-base sm:text-lg py-4 sm:py-6"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Start Partnership Discussion'}
              </Button>
              <Button 
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting}
                className="w-full sm:w-auto px-6 py-2"
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SponsorContactForm;
